/**
 * Client ของงานจัดซื้อ — ใบขอซื้อ (`/v1/purchase-requisitions`, D-084) และใบสั่งซื้อ
 * (`/v1/purchase-orders`, D-085) ตาม payload contract ที่ freeze ไว้ใน
 * `docs/API_CONTRACT.md`
 *
 * **ไฟล์เดียวสำหรับสองเอกสาร** โดยตั้งใจ ไม่ใช่ client คู่ขนานตัวที่สอง: ทั้งคู่เป็น
 * โมดูล `PROCUREMENT` เดียวกัน ถูก gate ด้วยตารางสิทธิ์แถวเดียวกัน ตอบ error envelope
 * รูปเดียวกัน และ **ใบสั่งซื้อกินใบขอซื้อเป็นวัตถุดิบ** — หน้าจอใบสั่งซื้ออ่านใบขอซื้อ
 * ที่อนุมัติแล้วผ่าน `fetchPurchaseRequisitions()` ตัวเดียวกับที่หน้าจอใบขอซื้อใช้
 * ไม่ใช่ผ่าน endpoint ที่คิดขึ้นใหม่ ท่อของคำขอ (`requestPayload`) และชุดสถานะความ
 * ล้มเหลวจึงมีชุดเดียว
 *
 * หลักการเดียวกับ `lib/suppliers/client.ts` และ `lib/bom/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีใบขอซื้อ"** — รายการว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่ายังไม่มีใครขอซื้อของชิ้นนี้
 *   แล้วเปิดใบซ้ำเข้าไปอีกใบ
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * contract ของ **ใบขอซื้อ** freeze ไว้หกเส้นทาง และไฟล์นี้ implement ห้า — `GET
 * /v1/purchase-requisitions/{pr_id}` ยังไม่มีฟังก์ชันของมัน เพราะยังไม่มีหน้าจอไหน
 * อ่านใบเดียวจากเซิร์ฟเวอร์: ปุ่มในแต่ละแถวกระทำต่อ **แถวที่ list ส่งมาแล้ว** และ
 * คำตอบของ transition คือใบฉบับล่าสุดอยู่แล้ว ฟังก์ชันที่เขียนทิ้งไว้ก่อนวันที่มีคน
 * เรียกใช้คือปุ่มที่ไม่มีใครทดสอบ (เหตุผลเดียวกับที่ `lib/suppliers/client.ts` เว้น
 * `GET /v1/suppliers/{supplier_id}` ไว้)
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreatePurchaseOrderBody,
  CreatePurchaseRequisitionBody,
  CreatePurchaseRequisitionHeaderBody,
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderListPage,
  PurchaseOrderTransitionBody,
  PurchaseRequisition,
  PurchaseRequisitionHeader,
  PurchaseRequisitionListPage,
  PurchaseRequisitionTransitionBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const PURCHASE_REQUISITIONS_PATH = '/v1/purchase-requisitions';
export const PURCHASE_REQUISITION_HEADERS_PATH = '/v1/purchase-requisition-headers';

/**
 * เส้นทางของใบเดียว — `pr_id` เป็น **UUID surrogate** (D-039) ไม่ใช่ `pr_number`
 * ซึ่งเป็น business key ที่ใบสั่งซื้ออ้าง
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทางมา
 * ถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือวิธีที่
 * คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้ — ซึ่งในกลุ่มนี้
 * แปลว่า `POST` ที่ตั้งใจจะ "ส่งอนุมัติ" ไปโผล่ที่เส้นอื่น
 */
export function purchaseRequisitionPath(prId: string): string {
  return `${PURCHASE_REQUISITIONS_PATH}/${encodeURIComponent(prId)}`;
}

/** สาม transition ของ lifecycle — ชื่อ segment ตรงตามที่ contract เขียนไว้ */
export type PurchaseRequisitionTransition = 'submit' | 'approve' | 'cancel';

export function purchaseRequisitionTransitionPath(
  prId: string,
  transition: PurchaseRequisitionTransition,
): string {
  return `${purchaseRequisitionPath(prId)}/${transition}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/purchase-requisitions` ที่ contract รับ — และไม่มีตัวที่ห้า
 *
 * **ไม่มีพารามิเตอร์ค้นหาเลย** โดยตั้งใจของ contract ช่องค้นหาบนหน้าจอจึงกรอง
 * **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ใช่ยิงคำขอใหม่ (ดู `procurementView.ts`)
 *
 * `plant_code` **แคบลงได้เท่านั้น ไม่เคยกว้างขึ้น** — เซิร์ฟเวอร์กรองตาม
 * `plant_scope` ของผู้เรียกก่อนเสมอ และโรงงานนอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface PurchaseRequisitionListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่ "หน้าแรก"
 * สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 */
export function buildPurchaseRequisitionListQuery(
  query: PurchaseRequisitionListQuery = {},
): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก
   * `PURCHASE_REQUISITION_STATUS_CHANGED` ออกจาก
   * `PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED` — ทั้งคู่มาด้วย `409` เท่ากัน
   * และทั้งคู่พา `details.current_status` มาด้วย
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้มีสองความหมายที่ `details.rule` เป็นตัวแยก: `PLANT_OUTSIDE_SCOPE`
 * คือ **โรงงานที่ขอไม่อยู่ในขอบเขตของคุณ** (D-004) ส่วนไม่มี rule คือ **แผนกของคุณ
 * ไม่ได้ถือโมดูล `PROCUREMENT` ในระดับนั้น** — คนละเรื่องกันคนละทางแก้ และเพราะ
 * `PROCUREMENT` เป็นโมดูลธุรกิจปกติ `ADMIN` ก็ถูกปฏิเสธด้วย `403` เช่นกัน (D-060)
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: มันคือ
 * "เลขใบซ้ำ" หรือ "มีคนกดไปก่อนแล้ว" ซึ่งผู้ใช้จัดการต่อเองได้ ส่วน "เซิร์ฟเวอร์
 * ทำงานไม่สำเร็จ" คือสิ่งที่เขาทำอะไรไม่ได้เลย
 *
 * `rule-violation` (`422`) เป็นที่อยู่ของกฎที่แพงที่สุดในกลุ่มนี้ —
 * `APPROVER_MUST_DIFFER_FROM_CREATOR` (D-010) การกลืนมันเป็น `forbidden` จะส่งผู้กด
 * ไปขอสิทธิ์เพิ่มเพื่อแก้สิ่งที่ทำงานถูกต้องอยู่แล้ว
 */
export type PurchaseRequisitionFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type PurchaseRequisitionListResult =
  | { readonly status: 'ready'; readonly page: PurchaseRequisitionListPage }
  | PurchaseRequisitionFailure;

export type PurchaseRequisitionWriteResult =
  | { readonly status: 'ready'; readonly requisition: PurchaseRequisition }
  | PurchaseRequisitionFailure;

export type PurchaseRequisitionHeaderWriteResult =
  | { readonly status: 'ready'; readonly header: PurchaseRequisitionHeader }
  | PurchaseRequisitionFailure;

export interface PurchaseRequisitionRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ: คีย์ที่หายไปทั้งคีย์
 * ถือเป็น `null` เพราะ contract ประกาศฟิลด์เหล่านี้เป็น optional ส่วนคีย์ที่มาเป็น
 * ตัวเลขหรือ object คือ payload ที่หน้าจอไม่ควรแสดง
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่างจะวน
 * ขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * หนึ่งใบขอซื้อ — **บังคับครบทุกฟิลด์ที่ contract บังคับ**
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของห้าค่าที่หน้าจอรู้จัก:
 * CHECK constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่หกทิ้งตรงนี้
 * จะทำให้ **ใบที่มีอยู่จริงหายไปจากรายการ** ในวันที่ชุดนั้นถูกขยาย ซึ่งแย่กว่าการ
 * แสดงรหัสที่หน้าจอยังไม่มีคำอธิบายให้ (ดู `purchaseRequisitionStatusLabel`)
 *
 * `quantity` ถูกอ่านเป็นสตริงตามที่ส่งมา **ไม่แปลงเป็น number แม้แต่ชั่วคราว** —
 * `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ
 */
export function parsePurchaseRequisition(raw: unknown): PurchaseRequisition | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const prId = requiredString(record.pr_id);
  const prNumber = requiredString(record.pr_number);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  const requiredDate = requiredString(record.required_date);
  const status = requiredString(record.status);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    prId === null ||
    prNumber === null ||
    plantId === null ||
    plantCode === null ||
    productId === null ||
    productCode === null ||
    quantity === null ||
    uomCode === null ||
    requiredDate === null ||
    status === null ||
    createdBy === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const suggestedSupplierId = nullableString(record.suggested_supplier_id);
  const suggestedSupplierCode = nullableString(record.suggested_supplier_code);
  const submittedAt = nullableString(record.submitted_at);
  const approvedBy = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  const cancelledAt = nullableString(record.cancelled_at);
  const note = nullableString(record.note);
  const headerNumber = record.header_number === undefined ? null : nullableString(record.header_number);
  const lineNo =
    record.line_no === undefined || record.line_no === null
      ? null
      : typeof record.line_no === 'number' && Number.isInteger(record.line_no)
        ? record.line_no
        : undefined;

  if (
    headerNumber === undefined ||
    lineNo === undefined ||
    suggestedSupplierId === undefined ||
    suggestedSupplierCode === undefined ||
    submittedAt === undefined ||
    approvedBy === undefined ||
    approvedAt === undefined ||
    cancelledAt === undefined ||
    note === undefined
  ) {
    return null;
  }

  return {
    pr_id: prId,
    pr_number: prNumber,
    plant_id: plantId,
    plant_code: plantCode,
    product_id: productId,
    product_code: productCode,
    quantity,
    uom_code: uomCode,
    required_date: requiredDate,
    suggested_supplier_id: suggestedSupplierId,
    suggested_supplier_code: suggestedSupplierCode,
    status,
    created_by_employee_id: createdBy,
    submitted_at: submittedAt,
    approved_by_employee_id: approvedBy,
    approved_at: approvedAt,
    cancelled_at: cancelledAt,
    note,
    created_at: createdAt,
    updated_at: updatedAt,
    // Only present when the server sent them (D-180): an older payload parses exactly as it did.
    ...(headerNumber === null && lineNo === null
      ? {}
      : { header_number: headerNumber, line_no: lineNo }),
  };
}

/**
 * อ่านหัวกระดาษใบขอซื้อ (D-180) — รายการเดียวที่อ่านไม่ออกทำให้ทั้งใบผิดรูป ไม่ถูกตัดทิ้งเงียบ ๆ
 */
export function parsePurchaseRequisitionHeader(raw: unknown): PurchaseRequisitionHeader | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const headerNumber = requiredString(record.header_number);
  const plantCode = requiredString(record.plant_code);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const remarks = nullableString(record.remarks);
  if (
    headerNumber === null ||
    plantCode === null ||
    createdBy === null ||
    createdAt === null ||
    remarks === undefined ||
    !Array.isArray(record.items)
  ) {
    return null;
  }
  const items: PurchaseRequisition[] = [];
  for (const entry of record.items) {
    const item = parsePurchaseRequisition(entry);
    if (item === null) {
      return null;
    }
    items.push(item);
  }
  return {
    header_number: headerNumber,
    plant_code: plantCode,
    remarks,
    created_by_employee_id: createdBy,
    created_at: createdAt,
    items,
  };
}

/**
 * body ของ `POST /v1/purchase-requisition-headers` — เฉพาะคีย์ที่ schema รับ (`extra = "forbid"`)
 * ไม่มีเลขใบและไม่มีสถานะ: เลขเป็นของเซิร์ฟเวอร์ รายการว่างของช่องไม่บังคับหายไปทั้งคีย์
 */
export function serializeCreatePurchaseRequisitionHeaderBody(
  body: CreatePurchaseRequisitionHeaderBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    ...(body.remarks === undefined ? {} : { remarks: body.remarks }),
    items: body.items.map((item) => ({
      product_code: item.product_code,
      quantity: item.quantity,
      uom_code: item.uom_code,
      required_date: item.required_date,
      ...(item.suggested_supplier_code === undefined
        ? {}
        : { suggested_supplier_code: item.suggested_supplier_code }),
      ...(item.note === undefined ? {} : { note: item.note }),
    })),
  };
}

/**
 * อ่านรายการจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ **ทั้งหน้าผิดรูป** ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: รายการที่ขาด
 * ไปหนึ่งแถวคือหน้าจอที่บอกผู้ใช้ว่ายังไม่มีใครขอซื้อของชิ้นนั้น แล้วเขาจะเปิดใบซ้ำ
 */
export function parsePurchaseRequisitionListPage(
  raw: unknown,
): PurchaseRequisitionListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: PurchaseRequisition[] = [];
  for (const entry of record.items) {
    const parsed = parsePurchaseRequisition(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/purchase-requisitions` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม ด้วยเหตุผลเดียวกับ
 * `serializeCreateSupplierBody()`: `extra = "forbid"` ทำให้คีย์แปลกปลอมที่ติดมา
 * (เช่นแถวของใบขอซื้อที่ถูก spread ทั้งก้อนพร้อม `status` หรือ `pr_id`) กลายเป็น
 * `400` ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และเป็นด่านที่ทำให้ฟิลด์ที่เซิร์ฟเวอร์เป็น
 * เจ้าของไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** ต่อให้ผู้เรียกถือ object ที่มีคีย์นั้นอยู่ในมือ
 *
 * ที่แพงที่สุดคือสองคีย์นี้: `status` ที่หลุดไปแปลว่ามีคนพยายามข้าม workflow และ
 * `created_by_employee_id` ที่หลุดไปแปลว่า D-010 ถูกเทียบกับค่าที่ client แต่งขึ้น
 *
 * สองคีย์ท้ายหายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง
 */
export function serializeCreatePurchaseRequisitionBody(
  body: CreatePurchaseRequisitionBody,
): Record<string, unknown> {
  return {
    pr_number: body.pr_number,
    plant_code: body.plant_code,
    product_code: body.product_code,
    quantity: body.quantity,
    uom_code: body.uom_code,
    required_date: body.required_date,
    ...(body.suggested_supplier_code === undefined
      ? {}
      : { suggested_supplier_code: body.suggested_supplier_code }),
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

/**
 * แปลง body ของ transition — คีย์เดียว และหายไปทั้งคีย์เมื่อไม่ได้ระบุ
 *
 * body ที่เป็น object ว่างเป็นคำขอที่ถูกต้องตาม contract (ด่าน staleness ปิดอยู่)
 * จึงไม่ต้องกันอะไรเป็นพิเศษ
 */
export function serializeTransitionBody(
  body: PurchaseRequisitionTransitionBody = {},
): Record<string, unknown> {
  return body.expected_status === undefined
    ? {}
    : { expected_status: body.expected_status };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | PurchaseRequisitionFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: PurchaseRequisitionRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** คำตอบของทุกเส้นทางที่เขียน — หนึ่งใบขอซื้อฉบับล่าสุดตามที่เซิร์ฟเวอร์เก็บไว้จริง */
function toWriteResult(result: PayloadResult): PurchaseRequisitionWriteResult {
  if (result.status !== 'payload') {
    return result;
  }
  const requisition = parsePurchaseRequisition(result.payload);
  return requisition === null
    ? { status: 'invalid-response' }
    : { status: 'ready', requisition };
}

/**
 * `GET /v1/purchase-requisitions` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ**
 *
 * **ไม่เรียงลำดับซ้ำเอง** contract ระบุว่าเซิร์ฟเวอร์เรียงตาม `pr_number` การเรียงซ้ำ
 * ที่ frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจนไม่มีใครเห็น และจะทำให้ cursor ของ
 * หน้าถัดไปไม่ต่อกับสิ่งที่ผู้ใช้เห็นอยู่
 */
export async function fetchPurchaseRequisitions(
  query: PurchaseRequisitionListQuery = {},
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionListResult> {
  const result = await requestPayload(
    `${PURCHASE_REQUISITIONS_PATH}${buildPurchaseRequisitionListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parsePurchaseRequisitionListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/purchase-requisitions` — เปิดใบใหม่ที่สถานะ `DRAFT` ตอบ `201`
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การเปิดใบขอซื้อไม่เคลื่อน
 * สต๊อกหรือบัญชี และเลขที่ซ้ำถูกกันด้วย unique constraint อยู่แล้ว
 * (`409 PR_NUMBER_TAKEN` โดยไม่เขียนอะไรเลย)
 */
export async function createPurchaseRequisition(
  body: CreatePurchaseRequisitionBody,
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionWriteResult> {
  return toWriteResult(
    await requestPayload(
      PURCHASE_REQUISITIONS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreatePurchaseRequisitionBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/purchase-requisition-headers` — เปิดหัวกระดาษพร้อมทุกรายการ ตอบ `201`
 *
 * ทุกรายการลงพร้อมกันหรือไม่ลงเลย; รายการที่ผิดถูกบอกลำดับใน `details.item_no`
 */
export async function createPurchaseRequisitionHeader(
  body: CreatePurchaseRequisitionHeaderBody,
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionHeaderWriteResult> {
  const result = await requestPayload(
    PURCHASE_REQUISITION_HEADERS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreatePurchaseRequisitionHeaderBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const header = parsePurchaseRequisitionHeader(result.payload);
  return header === null ? { status: 'invalid-response' } : { status: 'ready', header };
}

async function requestTransition(
  prId: string,
  transition: PurchaseRequisitionTransition,
  body: PurchaseRequisitionTransitionBody,
  options: PurchaseRequisitionRequestOptions,
): Promise<PurchaseRequisitionWriteResult> {
  return toWriteResult(
    await requestPayload(
      purchaseRequisitionTransitionPath(prId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeTransitionBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/purchase-requisitions/{pr_id}/submit` — `DRAFT → SUBMITTED` (EDIT)
 *
 * ผู้เรียกอ่านสถานะใหม่จาก **คำตอบ** ไม่ใช่จากสิ่งที่ตัวเองเพิ่งส่งไป — เซิร์ฟเวอร์
 * เขียน `submitted_at` และแถว `audit_log` ในธุรกรรมเดียวกับการเปลี่ยนสถานะ
 */
export async function submitPurchaseRequisition(
  prId: string,
  body: PurchaseRequisitionTransitionBody = {},
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionWriteResult> {
  return requestTransition(prId, 'submit', body, options);
}

/**
 * `POST /v1/purchase-requisitions/{pr_id}/approve` — `SUBMITTED → APPROVED` (APPROVE)
 *
 * **ผู้อนุมัติต้องไม่ใช่ผู้สร้าง** (D-010) ผู้ที่ถือสิทธิ์ครบแต่เป็นผู้เปิดใบเองจะได้
 * `422 APPROVER_MUST_DIFFER_FROM_CREATOR` และ **ไม่มีอะไรถูกเขียนเลย** — คำตอบนั้น
 * เป็น `rule-violation` ไม่ใช่ `forbidden` ตรงตามที่เซิร์ฟเวอร์ตั้งใจแยกไว้
 */
export async function approvePurchaseRequisition(
  prId: string,
  body: PurchaseRequisitionTransitionBody = {},
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionWriteResult> {
  return requestTransition(prId, 'approve', body, options);
}

/**
 * `POST /v1/purchase-requisitions/{pr_id}/cancel` — `DRAFT|SUBMITTED → CANCELLED` (EDIT)
 *
 * ใบที่อนุมัติแล้วหรือถูกแปลงเป็นใบสั่งซื้อแล้ว **ยกเลิกที่นี่ไม่ได้** (`409` พร้อม
 * `details.current_status`) — ฝ่ายจัดซื้อลงมือไปแล้ว การถอยต้องเกิดที่ใบสั่งซื้อ
 */
export async function cancelPurchaseRequisition(
  prId: string,
  body: PurchaseRequisitionTransitionBody = {},
  options: PurchaseRequisitionRequestOptions = {},
): Promise<PurchaseRequisitionWriteResult> {
  return requestTransition(prId, 'cancel', body, options);
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: PurchaseRequisitionFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/**
 * อ่าน `details.current_status` จากความล้มเหลว — สถานะที่ใบนั้น **ถืออยู่จริง**
 *
 * ทั้ง `PURCHASE_REQUISITION_STATUS_CHANGED` และ
 * `PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED` พามันมาด้วยเสมอ และมันคือสิ่งเดียว
 * ที่ทำให้หน้าจอบอกผู้ใช้ได้ว่าปุ่มที่เขากดหายไปเพราะอะไร แทนที่จะบอกแค่ว่าไม่สำเร็จ
 */
export function currentStatusFromFailure(
  failure: PurchaseRequisitionFailure,
): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/* ==========================================================================
 * ใบสั่งซื้อ (Purchase Order) — `/v1/purchase-orders` (D-085)
 *
 * เจ็ด operation ของ contract และไฟล์นี้ implement หก —
 * `GET /v1/purchase-orders/{po_id}` ยังไม่มีฟังก์ชันของมัน ด้วยเหตุผลเดียวกับที่
 * ใบขอซื้อเว้น `GET /{pr_id}` ไว้: ปุ่มในแต่ละแถวกระทำต่อ **แถวที่ list ส่งมาแล้ว**
 * และคำตอบของทุกเส้นที่เขียนคือใบฉบับล่าสุดพร้อมบรรทัดครบอยู่แล้ว
 *
 * ทุกเส้นเดินผ่าน `requestPayload` ตัวเดียวกับใบขอซื้อ จึงได้ `apiFetch` เส้นเดียวกัน
 * (header `Authorization` ถูกเติมที่นั่นจุดเดียว) และชุดความล้มเหลวชุดเดียวกัน
 * ========================================================================== */

/** ชุดความล้มเหลวของทั้งสองเอกสาร — ชุดเดียวกัน เพราะ envelope และ HTTP code ชุดเดียวกัน */
export type ProcurementFailure = PurchaseRequisitionFailure;
export type PurchaseOrderFailure = ProcurementFailure;
export type ProcurementRequestOptions = PurchaseRequisitionRequestOptions;
export type PurchaseOrderRequestOptions = ProcurementRequestOptions;

export const PURCHASE_ORDERS_PATH = '/v1/purchase-orders';

/**
 * เส้นทางของใบเดียว — `po_id` เป็น **UUID surrogate** (D-039) ไม่ใช่ `po_number`
 *
 * `encodeURIComponent` ด้วยเหตุผลเดียวกับ `purchaseRequisitionPath()`: ค่าที่อ่านมา
 * จาก payload แล้วแปะลง URL ดิบ ๆ คือวิธีที่ `POST` จะไปโผล่ที่เส้นอื่น ซึ่งในกลุ่มนี้
 * แปลว่าใบที่ตั้งใจจะ "ส่งให้ผู้ขาย" ถูกยกเลิกแทน
 */
export function purchaseOrderPath(poId: string): string {
  return `${PURCHASE_ORDERS_PATH}/${encodeURIComponent(poId)}`;
}

/** สี่ transition ของ lifecycle — ชื่อ segment ตรงตามที่ contract เขียนไว้ */
export type PurchaseOrderTransition = 'approve' | 'send' | 'acknowledge' | 'cancel';

export function purchaseOrderTransitionPath(
  poId: string,
  transition: PurchaseOrderTransition,
): string {
  return `${purchaseOrderPath(poId)}/${transition}`;
}

/**
 * เส้นทางของ "วันส่งที่ Supplier ยืนยัน" ต่อบรรทัด (D-163 ข้อ 2) —
 * `PUT /v1/purchase-orders/{po_id}/lines/{line_no}/confirmed-date`
 *
 * `line_no` เป็นเลขลำดับบรรทัดจริง ไม่ใช่ค่าที่อ่านมาจาก payload แบบอิสระ จึงไม่ต้อง
 * `encodeURIComponent` เหมือน `po_id`
 */
export function purchaseOrderConfirmedDatePath(poId: string, lineNo: number): string {
  return `${purchaseOrderPath(poId)}/lines/${lineNo}/confirmed-date`;
}

/**
 * พารามิเตอร์ของ `GET /v1/purchase-orders` ที่ contract รับ
 *
 * มี `supplier_code` เพิ่มมาจากของใบขอซื้อ และ **ยังไม่มีพารามิเตอร์ค้นหาอิสระ** —
 * ช่องค้นหาบนหน้าจอจึงกรองฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว (ดู `procurementView.ts`)
 *
 * `plant_code` **แคบลงได้เท่านั้น ไม่เคยกว้างขึ้น** — โรงงานนอกขอบเขตตอบ `403`
 * ไม่ใช่หน้าว่าง
 */
export interface PurchaseOrderListQuery {
  readonly plant_code?: string;
  readonly supplier_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildPurchaseOrderListQuery(query: PurchaseOrderListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const supplierCode = query.supplier_code?.trim() ?? '';
  if (supplierCode !== '') {
    params.append('supplier_code', supplierCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export type PurchaseOrderListResult =
  | { readonly status: 'ready'; readonly page: PurchaseOrderListPage }
  | PurchaseOrderFailure;

export type PurchaseOrderWriteResult =
  | { readonly status: 'ready'; readonly order: PurchaseOrder }
  | PurchaseOrderFailure;

/* -------------------------------------------------------------- parsing -- */

/**
 * `supplier_confirmed_date` — `null` ครอบทั้ง "ยังไม่มีใครกรอก" และ "เซิร์ฟเวอร์รุ่นเก่า
 * ยังไม่ส่งคีย์นี้มา" (คีย์ที่หายไปทั้งคีย์ถือเป็น `null` เหมือนฟิลด์ nullable อื่น ๆ
 * — เซิร์ฟเวอร์รุ่นเก่าไม่ทำให้หน้าจอพัง) ค่าที่มาเป็นสตริงแต่ไม่ตรงรูป `YYYY-MM-DD`
 * ถือว่าผิดรูป — ปฏิเสธทั้งบรรทัดตามแบบ parser เดิม ไม่ใช่ทิ้งแค่ฟิลด์นี้
 */
function nullableDateOnlyString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/**
 * หนึ่งบรรทัด — **บังคับครบทุกฟิลด์** และจำนวนทุกตัวต้องเป็นสตริง
 *
 * `line_no` เป็นจำนวนเต็มจริง ๆ (ลำดับบรรทัด ไม่ใช่ปริมาณ) จึงอ่านเป็น number ส่วน
 * `quantity`/`unit_price`/`line_amount` อ่านเป็นสตริงตามที่ส่งมา **ไม่แปลงแม้แต่
 * ชั่วคราว** — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ และยอดที่
 * ปัดแล้วคือยอดที่ผู้ขายไม่ได้ตกลงด้วย
 */
export function parsePurchaseOrderLine(raw: unknown): PurchaseOrderLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = record.line_no;
  if (typeof lineNo !== 'number' || !Number.isInteger(lineNo)) {
    return null;
  }

  const prId = requiredString(record.pr_id);
  const prNumber = requiredString(record.pr_number);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  const unitPrice = requiredString(record.unit_price);
  const lineAmount = requiredString(record.line_amount);

  if (
    prId === null ||
    prNumber === null ||
    productId === null ||
    productCode === null ||
    quantity === null ||
    uomCode === null ||
    unitPrice === null ||
    lineAmount === null
  ) {
    return null;
  }

  const supplierConfirmedDate = nullableDateOnlyString(record.supplier_confirmed_date);
  if (supplierConfirmedDate === undefined) {
    return null;
  }

  return {
    line_no: lineNo,
    pr_id: prId,
    pr_number: prNumber,
    product_id: productId,
    product_code: productCode,
    quantity,
    uom_code: uomCode,
    unit_price: unitPrice,
    line_amount: lineAmount,
    supplier_confirmed_date: supplierConfirmedDate,
  };
}

/**
 * ใบสั่งซื้อหนึ่งใบ — **บรรทัดต้องอ่านออกทุกบรรทัด ไม่งั้นทั้งใบผิดรูป**
 *
 * บรรทัดที่หายไปเงียบ ๆ หนึ่งบรรทัดคือคำสั่งซื้อที่หน้าจอบอกว่าสั่งของน้อยกว่าที่สั่งจริง
 * และยอดรวมที่ผู้ใช้เห็นจะไม่ตรงกับเอกสารที่ผู้ขายได้รับ
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของเจ็ดค่า — CHECK
 * constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่แปดทิ้งจะทำให้
 * **ใบที่มีอยู่จริงหายไปจากรายการ**
 */
export function parsePurchaseOrder(raw: unknown): PurchaseOrder | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const poId = requiredString(record.po_id);
  const poNumber = requiredString(record.po_number);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const supplierId = requiredString(record.supplier_id);
  const supplierCode = requiredString(record.supplier_code);
  const status = requiredString(record.status);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    poId === null ||
    poNumber === null ||
    plantId === null ||
    plantCode === null ||
    supplierId === null ||
    supplierCode === null ||
    status === null ||
    createdBy === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const approvedBy = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  const sentAt = nullableString(record.sent_at);
  const acknowledgedAt = nullableString(record.acknowledged_at);
  const cancelledAt = nullableString(record.cancelled_at);
  const note = nullableString(record.note);

  if (
    approvedBy === undefined ||
    approvedAt === undefined ||
    sentAt === undefined ||
    acknowledgedAt === undefined ||
    cancelledAt === undefined ||
    note === undefined
  ) {
    return null;
  }

  if (!Array.isArray(record.lines)) {
    return null;
  }
  const lines: PurchaseOrderLine[] = [];
  for (const entry of record.lines) {
    const line = parsePurchaseOrderLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return {
    po_id: poId,
    po_number: poNumber,
    plant_id: plantId,
    plant_code: plantCode,
    supplier_id: supplierId,
    supplier_code: supplierCode,
    status,
    created_by_employee_id: createdBy,
    approved_by_employee_id: approvedBy,
    approved_at: approvedAt,
    sent_at: sentAt,
    acknowledged_at: acknowledgedAt,
    cancelled_at: cancelledAt,
    note,
    lines,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** อ่านรายการจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น** */
export function parsePurchaseOrderListPage(raw: unknown): PurchaseOrderListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: PurchaseOrder[] = [];
  for (const entry of record.items) {
    const parsed = parsePurchaseOrder(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/purchase-orders` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread — เหตุผลเดียวกับ
 * `serializeCreatePurchaseRequisitionBody()` แต่ในกลุ่มนี้ด่านสำคัญอยู่ที่ **บรรทัด**:
 * แต่ละบรรทัดถูกประกอบใหม่ให้เหลือ `pr_number` + `unit_price` เท่านั้น ต่อให้ผู้เรียก
 * ถือแถวของใบขอซื้อทั้งก้อนอยู่ในมือ (ซึ่งมี `product_code`/`quantity`/`uom_code`
 * ติดมาด้วยเสมอ) — คีย์เหล่านั้นจะไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** และนั่นคือสิ่งที่กัน
 * `400 PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION` ที่ผู้ใช้แก้เองไม่ได้
 *
 * `note` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง
 */
export function serializeCreatePurchaseOrderBody(
  body: CreatePurchaseOrderBody,
): Record<string, unknown> {
  return {
    po_number: body.po_number,
    plant_code: body.plant_code,
    supplier_code: body.supplier_code,
    ...(body.note === undefined ? {} : { note: body.note }),
    lines: body.lines.map((line) => ({
      pr_number: line.pr_number,
      unit_price: line.unit_price,
    })),
  };
}

/** แปลง body ของ transition — คีย์เดียว และหายไปทั้งคีย์เมื่อไม่ได้ระบุ */
export function serializePurchaseOrderTransitionBody(
  body: PurchaseOrderTransitionBody = {},
): Record<string, unknown> {
  return body.expected_status === undefined
    ? {}
    : { expected_status: body.expected_status };
}

/* ------------------------------------------------------------- requests -- */

/** คำตอบของทุกเส้นทางที่เขียน — หนึ่งใบสั่งซื้อฉบับล่าสุดพร้อมบรรทัดครบ */
function toOrderWriteResult(result: PayloadResult): PurchaseOrderWriteResult {
  if (result.status !== 'payload') {
    return result;
  }
  const order = parsePurchaseOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/**
 * `GET /v1/purchase-orders` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ**
 *
 * **ไม่เรียงลำดับซ้ำเอง** contract ระบุว่าเซิร์ฟเวอร์เรียงตาม `po_number`
 */
export async function fetchPurchaseOrders(
  query: PurchaseOrderListQuery = {},
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderListResult> {
  const result = await requestPayload(
    `${PURCHASE_ORDERS_PATH}${buildPurchaseOrderListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parsePurchaseOrderListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/purchase-orders` — รวมใบขอซื้อที่อนุมัติแล้วเป็นใบสั่งซื้อ `DRAFT` (`201`)
 *
 * เป็น **ธุรกรรมเดียว**: ใบ บรรทัด และการ flip ใบขอซื้อทุกใบเป็น `CONVERTED_TO_PO`
 * ล้มจุดใดก็ไม่เหลืออะไรเลย (D-085) ผู้เรียกจึงไม่ต้องตามเก็บกวาดเมื่อถูกปฏิเสธ
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้เส้นทางที่กระทบ
 * stock/บัญชี การออกใบสั่งซื้อยังไม่เคลื่อนสต๊อก และเลขซ้ำถูกกันด้วย
 * `409 PO_NUMBER_TAKEN` โดยไม่เขียนอะไรเลย
 */
export async function createPurchaseOrder(
  body: CreatePurchaseOrderBody,
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return toOrderWriteResult(
    await requestPayload(
      PURCHASE_ORDERS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreatePurchaseOrderBody(body)),
      },
      options,
    ),
  );
}

async function requestOrderTransition(
  poId: string,
  transition: PurchaseOrderTransition,
  body: PurchaseOrderTransitionBody,
  options: PurchaseOrderRequestOptions,
): Promise<PurchaseOrderWriteResult> {
  return toOrderWriteResult(
    await requestPayload(
      purchaseOrderTransitionPath(poId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializePurchaseOrderTransitionBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/purchase-orders/{po_id}/approve` — `DRAFT → APPROVED` (APPROVE)
 *
 * **ผู้อนุมัติต้องไม่ใช่ผู้สร้าง** (D-010) ผู้ที่ถือสิทธิ์ครบแต่เป็นผู้เปิดใบเองได้
 * `422 APPROVER_MUST_DIFFER_FROM_CREATOR` และ **ไม่มีอะไรถูกเขียนเลย** — เป็น
 * `rule-violation` ไม่ใช่ `forbidden` ตรงตามที่เซิร์ฟเวอร์ตั้งใจแยกไว้
 */
export async function approvePurchaseOrder(
  poId: string,
  body: PurchaseOrderTransitionBody = {},
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return requestOrderTransition(poId, 'approve', body, options);
}

/** `POST /v1/purchase-orders/{po_id}/send` — `APPROVED → SENT` (EDIT) */
export async function sendPurchaseOrder(
  poId: string,
  body: PurchaseOrderTransitionBody = {},
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return requestOrderTransition(poId, 'send', body, options);
}

/** `POST /v1/purchase-orders/{po_id}/acknowledge` — `SENT → ACKNOWLEDGED` (EDIT) */
export async function acknowledgePurchaseOrder(
  poId: string,
  body: PurchaseOrderTransitionBody = {},
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return requestOrderTransition(poId, 'acknowledge', body, options);
}

/**
 * `POST /v1/purchase-orders/{po_id}/cancel` — สี่สถานะแรก → `CANCELLED` (EDIT)
 *
 * **ไม่คืนใบขอซื้อกลับเป็น `APPROVED`** (D-085) ใบขอซื้อยังอยู่ที่ `CONVERTED_TO_PO`
 * และการสั่งซื้อใหม่ต้องเปิดใบขอซื้อใบใหม่ — สถานะของความต้องการจึงไม่เด้งขึ้นลง
 */
export async function cancelPurchaseOrder(
  poId: string,
  body: PurchaseOrderTransitionBody = {},
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return requestOrderTransition(poId, 'cancel', body, options);
}

/** body ของ `PUT .../confirmed-date` — ฟิลด์เดียวและ **บังคับ** ล้างค่าส่ง `null` ไม่ใช่คีย์ที่หายไป */
function serializeSetConfirmedDateBody(date: string | null): Record<string, unknown> {
  return { supplier_confirmed_date: date };
}

/**
 * `PUT /v1/purchase-orders/{po_id}/lines/{line_no}/confirmed-date` — บันทึก "วันส่งที่
 * Supplier ยืนยัน" ของบรรทัดเดียว (D-163 ข้อ 2, EDIT)
 *
 * ใช้ได้เฉพาะ PO สถานะ `SENT`/`ACKNOWLEDGED`/`PARTIALLY_RECEIVED` — สถานะอื่นตอบ
 * `422 PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS` คำตอบคือใบฉบับล่าสุดทั้งใบ
 * (`PurchaseOrderResponse`) เหมือน transition อื่นทุกเส้น
 */
export async function setSupplierConfirmedDate(
  poId: string,
  lineNo: number,
  date: string | null,
  options: PurchaseOrderRequestOptions = {},
): Promise<PurchaseOrderWriteResult> {
  return toOrderWriteResult(
    await requestPayload(
      purchaseOrderConfirmedDatePath(poId, lineNo),
      {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeSetConfirmedDateBody(date)),
      },
      options,
    ),
  );
}
